import React from "react";
import { Info } from "lucide-react";
import { isProductMigrationNoticeEnabledForCycle } from "@/features/productMigrationFeature";

export interface ProductMigrationLink {
  productCode: string;
  productDescription?: string | null;
  isSameProduct: boolean;
  clientHadIt: boolean;
  clientPreviousPrice?: number | null;
}

export interface ProductMigrationInfo {
  targets: ProductMigrationLink[];
  sources: ProductMigrationLink[];
}

interface ProductMigrationNoticeProps {
  info?: ProductMigrationInfo | null;
  cycleYear?: number;
  formatCurrency: (value: number) => string;
}

const label = (link: ProductMigrationLink) =>
  link.productDescription ? `${link.productCode} - ${link.productDescription}` : link.productCode;

/**
 * Identificação temporária do de-para (product_migration):
 * - ciclo anterior: o produto é DE e aponta para o(s) produto(s) PARA do próximo ciclo;
 * - ciclo atual (2027+): o produto é PARA e o valor de referência/reajuste vem do(s) DE do cliente.
 */
export const ProductMigrationNotice: React.FC<ProductMigrationNoticeProps> = ({
  info,
  cycleYear,
  formatCurrency,
}) => {
  if (!info || !isProductMigrationNoticeEnabledForCycle(cycleYear)) return null;

  const targets = info.targets ?? [];
  const sources = info.sources ?? [];
  const isTargetCycle = (cycleYear ?? 0) >= 2027;

  if (!isTargetCycle && targets.length > 0) {
    const sameOnly = targets.every((t) => t.isSameProduct);
    return (
      <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-100 border border-yellow-400 rounded-md">
        <Info className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-yellow-900 space-y-1">
          <p className="font-semibold">De-para: produto DE (identificação temporária)</p>
          <p>
            {sameOnly
              ? `No ciclo ${(cycleYear ?? 0) + 1} este produto mantém o mesmo código.`
              : `No ciclo ${(cycleYear ?? 0) + 1} este produto aponta para:`}
          </p>
          {!sameOnly && (
            <ul className="list-disc pl-4">
              {targets.map((t) => (
                <li key={t.productCode}>{t.isSameProduct ? `${label(t)} (mesmo produto)` : label(t)}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  if (isTargetCycle && sources.length > 0) {
    const had = sources.filter((s) => s.clientHadIt);
    const sum = had.reduce((total, s) => total + (s.clientPreviousPrice ?? 0), 0);
    return (
      <div className="flex items-start gap-2 mt-2 p-3 bg-yellow-100 border border-yellow-400 rounded-md">
        <Info className="h-4 w-4 text-yellow-700 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-yellow-900 space-y-1">
          <p className="font-semibold">De-para: produto PARA (identificação temporária)</p>
          <p>Apontado por:</p>
          <ul className="list-disc pl-4">
            {sources.map((s) => (
              <li key={s.productCode}>
                {label(s)}
                {s.isSameProduct ? " (mesmo produto)" : ""} —{" "}
                {s.clientHadIt && s.clientPreviousPrice != null
                  ? `cliente tinha: ${formatCurrency(s.clientPreviousPrice)}`
                  : "cliente não tinha (fora da soma)"}
              </li>
            ))}
          </ul>
          <p>
            {had.length > 0
              ? `Valor de referência deve considerar a soma dos itens que o cliente tinha: ${formatCurrency(sum)}.`
              : "O cliente não tinha nenhum item do DE: referência oculta."}
          </p>
        </div>
      </div>
    );
  }

  return null;
};
